import { c as _c } from "react/compiler-runtime";
import type { RefObject } from "react";
import WaveSettingEditorActions from "./WaveSettingEditorActions";
type ApprovalThresholdTimeUnit = "minutes" | "hours";
interface WaveApprovalThresholdsEditorFormProps {
  readonly disabled: boolean;
  readonly inputRef: RefObject<HTMLInputElement | null>;
  readonly minTimeValue: string;
  readonly onCancel: () => void;
  readonly onMinTimeValueChange: (value: string) => void;
  readonly onSave: () => void;
  readonly onThresholdValueChange: (value: string) => void;
  readonly onUnitChange: (unit: ApprovalThresholdTimeUnit) => void;
  readonly thresholdValue: string;
  readonly unit: ApprovalThresholdTimeUnit;
}
const APPROVAL_THRESHOLD_TIME_UNITS = ["minutes", "hours"] as const;
const isApprovalThresholdTimeUnit = (value: string): value is ApprovalThresholdTimeUnit => APPROVAL_THRESHOLD_TIME_UNITS.some(unit => unit === value);
export default function WaveApprovalThresholdsEditorForm(t0) {
  const $ = _c(36);
  const {
    disabled,
    inputRef,
    minTimeValue,
    onCancel,
    onMinTimeValueChange,
    onSave,
    onThresholdValueChange,
    onUnitChange,
    thresholdValue,
    unit
  } = t0;
  let t1;
  if ($[0] !== onSave) {
    t1 = event => {
      event.preventDefault();
      onSave();
    };
    $[0] = onSave;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span>Threshold</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onThresholdValueChange) {
    t3 = event_1 => onThresholdValueChange(event_1.target.value);
    $[3] = onThresholdValueChange;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== disabled || $[6] !== inputRef || $[7] !== t3 || $[8] !== thresholdValue) {
    t4 = <label className="tw-flex tw-flex-col tw-gap-1.5 tw-text-sm tw-font-medium tw-text-iron-100">{t2}<input ref={inputRef} aria-label="Approval threshold value" autoComplete="off" autoFocus={true} className="tw-min-w-0 tw-rounded-md tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-2.5 tw-py-2 tw-text-sm tw-text-iron-100 focus:tw-border-primary-400 focus:tw-outline-none" disabled={disabled} inputMode="numeric" type="text" value={thresholdValue} onFocus={_temp} onChange={t3} /></label>;
    $[5] = disabled;
    $[6] = inputRef;
    $[7] = t3;
    $[8] = thresholdValue;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span>Min time</span>;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== onMinTimeValueChange) {
    t6 = event_3 => onMinTimeValueChange(event_3.target.value);
    $[11] = onMinTimeValueChange;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== disabled || $[14] !== minTimeValue || $[15] !== t6) {
    t7 = <input aria-label="Minimum time above threshold" autoComplete="off" className="tw-min-w-0 tw-flex-1 tw-rounded-md tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-2.5 tw-py-2 tw-text-sm tw-text-iron-100 focus:tw-border-primary-400 focus:tw-outline-none" disabled={disabled} inputMode="numeric" type="text" value={minTimeValue} onFocus={_temp2} onChange={t6} />;
    $[13] = disabled;
    $[14] = minTimeValue;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== onUnitChange) {
    t8 = event_4 => {
      if (isApprovalThresholdTimeUnit(event_4.target.value)) {
        onUnitChange(event_4.target.value);
      }
    };
    $[17] = onUnitChange;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = APPROVAL_THRESHOLD_TIME_UNITS.map(_temp3);
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== disabled || $[21] !== t8 || $[22] !== unit) {
    t10 = <select aria-label="Minimum time above threshold unit" className="tw-rounded-md tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-2.5 tw-py-2 tw-text-sm tw-text-iron-100 focus:tw-border-primary-400 focus:tw-outline-none" disabled={disabled} value={unit} onChange={t8}>{t9}</select>;
    $[20] = disabled;
    $[21] = t8;
    $[22] = unit;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t7) {
    t11 = <div className="tw-flex tw-flex-col tw-gap-1.5 tw-text-sm tw-font-medium tw-text-iron-100">{t5}<div className="tw-flex tw-gap-2">{t7}{t10}</div></div>;
    $[24] = t10;
    $[25] = t7;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <p className="tw-mb-0 tw-text-xs tw-leading-5 tw-text-iron-400">Leave min time blank for immediate approval.</p>;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== disabled || $[29] !== onCancel) {
    t13 = <WaveSettingEditorActions disabled={disabled} onCancel={onCancel} />;
    $[28] = disabled;
    $[29] = onCancel;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t1 || $[32] !== t11 || $[33] !== t13 || $[34] !== t4) {
    t14 = <form className="tw-flex tw-flex-col tw-gap-3" onSubmit={t1}>{t4}{t11}{t12}{t13}</form>;
    $[31] = t1;
    $[32] = t11;
    $[33] = t13;
    $[34] = t4;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
}
function _temp3(timeUnit) {
  return <option key={timeUnit} value={timeUnit}>{timeUnit}</option>;
}
function _temp2(event_2) {
  return event_2.currentTarget.select();
}
function _temp(event_0) {
  return event_0.currentTarget.select();
}
export type { ApprovalThresholdTimeUnit };